// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Zone from "@insite/client-framework/Components/Zone";
import FieldDefinition, { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Page from "@insite/mobius/Page";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useRowOverlap from "@stoneage/App/Hooks/useRowOverlap";
import { CustomFieldDefinition } from "@stoneage/CustomFieldDefinition";
import GridContainer, { GridContainerProps } from "@stoneage/MobiusOverrides/GridContainer/GridContainer";
import { constants, DivWrapper } from "@stoneage/XDL/constants";
import { fetchBackgroundImg, xScreen } from "@stoneage/XDL/Utilities";
import { xClassNames } from "@stoneage/XDL/Utilities/classNames";
import * as cssLinter from "css";
import * as React from "react";
import styled, { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    customCSS = "customCSS",
    // XNG-Changes
    fullWidth = "fullWidth",
    fullWidthContent = "fullWidthContent",
    spaceTop = "spaceTop",
    spaceBottom = "spaceBottom",
    spaceTopOnTablet = "spaceTopOnTablet",
    spaceBottomOnTablet = "spaceBottomOnTablet",
    backgroundColor = "backgroundColor",
    backgroundImage = "backgroundImage",
    mediumBackgroundImage = "mediumBackgroundImage",
    smallBackgroundImage = "smallBackgroundImage",
    rowId = "rowId",
    makeItemsTakeFullHeight = "makeItemsTakeFullHeight",
    allowOverlapOn = "allowOverlapOn",
    overlapPosition = "overlapPosition",
    overlapValue = "overlapValue",
    selectOverlapItem = "selectOverlapItem",
    gapOnTablet = "gapOnTablet",
    gapOnMobile = "gapOnMobile",
    addFooterSpace = "addFooterSpace",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.fullWidth]: boolean;
        [fields.fullWidthContent]: boolean;
        [fields.spaceTop]: string;
        [fields.spaceBottom]: string;
        [fields.spaceTopOnTablet]: string;
        [fields.spaceBottomOnTablet]: string;
        [fields.backgroundColor]: string;
        [fields.backgroundImage]: string;
        [fields.mediumBackgroundImage]: string;
        [fields.smallBackgroundImage]: string;
        [fields.rowId]: string;
        [fields.makeItemsTakeFullHeight]: boolean;
        [fields.allowOverlapOn]: "mobileTransparent" | "wideScreen" | "both" | "none";
        [fields.overlapPosition]: string;
        [fields.overlapValue]: string;
        [fields.selectOverlapItem]: string;
        [fields.gapOnTablet]: number;
        [fields.gapOnMobile]: number;
        [fields.addFooterSpace]: boolean;
    };
}

// XNG-Changes
const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [12, 3, 1, 1, 1],
    2: [12, 4, 2, 2, 2],
    3: [12, 6, 3, 3, 3],
    4: [12, 6, 4, 4, 4],
    5: [12, 6, 6, 5, 5],
    6: [12, 6, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

// XNG-Changes: adding styles
interface RowStyles {
    container?: InjectableCss;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
}

const rowStyles: RowStyles = {
    container: {
        css: css`
            ${({ allowOverlapOn, backgroundColor, canOverlap, overlapPosition, overlapValue }: any) => {
                if (!canOverlap) {
                    return undefined;
                }

                const overlap = Number(overlapValue);

                let cssStyle: any = css``;
                let cssMobileStyle: any = css``;

                if (overlapPosition === "both") {
                    cssStyle = css`
                        background: linear-gradient(
                            to bottom,
                            transparent 0%,
                            transparent ${(100 - overlap) / 2}%,
                            ${backgroundColor} ${(100 - overlap) / 2}%,
                            ${backgroundColor} ${overlap + (100 - overlap) / 2}%,
                            transparent ${overlap + (100 - overlap) / 2}%,
                            transparent 100%
                        );
                    `;
                }
                if (overlapPosition === "top") {
                    cssStyle = css`
                        background: linear-gradient(
                            to bottom,
                            transparent 0%,
                            transparent ${100 - overlap}%,
                            ${backgroundColor} ${100 - overlap}%,
                            ${backgroundColor} 100%
                        );
                    `;
                }
                if (overlapPosition === "bottom") {
                    cssStyle = css`
                        background: linear-gradient(to bottom, ${backgroundColor} ${overlap}%, transparent ${overlap}%);
                    `;
                }
                if (allowOverlapOn === "wideScreen") {
                    cssMobileStyle = css`
                        ${xScreen("lte", "sm")} {
                            background: ${backgroundColor};
                        }
                    `;
                }

                if (allowOverlapOn === "mobileTransparent") {
                    cssMobileStyle = css`
                        ${xScreen("lte", "sm")} {
                            background: transparent;
                        }
                    `;
                }

                return css`
                    ${cssStyle}
                    ${cssMobileStyle}
                `;
            }}
        `,
    },
    gridItem: {
        css: css`
            ${({ makeItemsTakeFullHeight }: any) =>
                makeItemsTakeFullHeight &&
                css`
                    & > ${DivWrapper} {
                        height: 100%;
                    }
                `}
            div[class^="ZoneWrapper"] {
                display: flex;
                flex-direction: column;
                ${({ align }: any) =>
                    align === "middle"
                        ? css`
                              justify-content: center;
                          `
                        : align === "top"
                          ? css`
                                justify-content: flex-start;
                            `
                          : align === "bottom" &&
                            css`
                                justify-content: flex-end;
                            `}
                div[class^="AddContainer"] {
                    width: 100%;
                }
            }
        `,
    },
    gridContainer: {
        css: css`
            &&& {
                max-width: 100%;
            }
        `,
    },
};

// XNG-Changes: adding RowStyleWrapper
const RowStyleWrapper = styled.div<
    {
        backgroundColor?: string;
        canOverlap?: boolean;
        allowOverlapOn?: "mobileTransparent" | "wideScreen" | "both" | "none";
        overlapPosition?: string;
        overlapValue?: string;
        rowId?: string;
        isFullWidth?: boolean;
        addFooterSpace?: boolean;
    } & InjectableCss<any>
>`
    ${({ backgroundColor }) =>
        backgroundColor &&
        css`
            background: ${backgroundColor};
        `}
    ${({ isFullWidth }) =>
        isFullWidth &&
        css`
            width: 100vw;
            position: relative;
            left: 50%;
            right: 50%;
            margin-left: -50vw;
            margin-right: -50vw;
        `}
    ${({ addFooterSpace }) =>
        addFooterSpace &&
        css`
            margin-bottom: ${constants.footer.offsetTop}px;
        `}
    
    scroll-margin-top: 100px;
    ${xScreen("lte", "sm")} {
        scroll-margin-top: 50px;
    }

    ${injectCss}
`;

// XNG-Changes
const defaultCustomCss = `
    &.full-width-row {
    }

    &.content-size-row {
    }
`;
// XNG-Changes
const Row: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: {
            columns,
            gap,
            alignment,
            reflow,
            customCSS,
            // XNG-Changes
            fullWidth,
            fullWidthContent,
            spaceTop,
            spaceBottom,
            spaceTopOnTablet,
            spaceBottomOnTablet,
            backgroundColor,
            backgroundImage,
            mediumBackgroundImage,
            smallBackgroundImage,
            rowId,
            makeItemsTakeFullHeight,
            allowOverlapOn,
            overlapPosition,
            overlapValue,
            selectOverlapItem,
            gapOnTablet,
            gapOnMobile,
            addFooterSpace,
        },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];

    // XNG-Changes: adding overlap and styles
    const isAllowOverlap = allowOverlapOn !== "none";
    const rowRef = React.createRef<HTMLDivElement | null>();
    const canOverlap = useRowOverlap(rowRef, isAllowOverlap);

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        const itemProps: { [key: string]: string | boolean } = {
            makeItemsTakeFullHeight,
        };

        // XNG-Changes: adding overlap
        const isOverlapItem =
            isAllowOverlap && (parseInt(selectOverlapItem, 10) - 1 === x || selectOverlapItem === "all");

        if (isOverlapItem) {
            itemProps["data-overlap"] = overlapValue;
        }

        columnElements.push(
            <GridItem
                key={x}
                width={reflow ? convertToResponsive(columns[x]) : columns[x]}
                align={align}
                className={xClassNames.append(isOverlapItem ? "overlap" : "no-overlap")}
                {...itemProps}
                {...rowStyles.gridItem}
            >
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    // XNG-Changes
    const getBackground = () => {
        if (backgroundImage || mediumBackgroundImage || smallBackgroundImage) {
            return css`
                background-image: ${fetchBackgroundImg(backgroundImage)};
                background-size: cover;
                background-repeat: no-repeat;
                background-position-x: center;

                ${xScreen("lte", "md")} {
                    background-image: ${fetchBackgroundImg(mediumBackgroundImage || backgroundImage)};
                }

                ${xScreen("lte", "xs")} {
                    background-image: ${fetchBackgroundImg(
                        smallBackgroundImage || mediumBackgroundImage || backgroundImage,
                    )};
                }
            `;
        }

        // return css`
        //     background-color: ${backgroundColor};
        // `;
    };

    // XNG-Changes
    const getPaddings = () => css`
        ${spaceTop &&
        css`
            padding-top: ${spaceTop};
        `};
        ${spaceBottom &&
        css`
            padding-bottom: ${spaceBottom};
        `};

        ${xScreen("lte", "sm")} {
            ${spaceTopOnTablet &&
            css`
                padding-top: ${spaceTopOnTablet};
            `};
            ${spaceBottomOnTablet &&
            css`
                padding-bottom: ${spaceBottomOnTablet};
            `};
        }
    `;

    const RowGrid = () => (
        <GridContainer
            gap={gap || 0}
            // XNG-Changes
            gapMobile={gapOnMobile}
            gapTablet={gapOnTablet ?? gap}
            css={css`
                ${rowStyles.gridContainer?.css}
            `}
            offsetCss={css`
                ${isAllowOverlap && `align-items: start;`}
            `}
        >
            {columnElements}
        </GridContainer>
    );

    // XNG-Changes
    return (
        <RowStyleWrapper
            ref={rowRef}
            backgroundColor={backgroundColor}
            canOverlap={canOverlap}
            allowOverlapOn={allowOverlapOn}
            overlapPosition={overlapPosition}
            overlapValue={overlapValue}
            isFullWidth={fullWidth}
            addFooterSpace={addFooterSpace}
            id={rowId}
            className={
                fullWidth
                    ? fullWidthContent
                        ? "full-width-row full-width-content"
                        : "full-width-row"
                    : "content-size-row"
            }
            css={css`
                ${fullWidth &&
                css`
                    display: flex;
                    justify-content: center;
                    &.full-width-content > div {
                        max-width: 100%;
                    }
                `}
                ${getPaddings()}
                ${isAllowOverlap ? rowStyles.container?.css : undefined}
                ${getBackground()}
                ${customCSS}
            `}
        >
            {fullWidth && !fullWidthContent ? (
                <Page
                    as="div"
                    css={css`
                        &&& {
                            overflow: visible;
                        }
                    `}
                >
                    <RowGrid />
                </Page>
            ) : (
                <RowGrid />
            )}
        </RowStyleWrapper>
    );
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

// XNG-Changes: adding options tab
const optionsTab = {
    displayName: "Options",
    sortOrder: 2,
};

// XNG-Changes
const spaceFieldDefinition = (name: string) =>
    ({
        name,
        editorTemplate: "TextField",
        fieldType: "General",
        defaultValue: "",
        tooltip: "padding value in pixels",
        placeholder: "Example: 20px",
        validate: (value: string | null) => {
            if (!value) {
                return null;
            }
            return CSS.supports("padding", value) ? null : `Invalid Input`;
        },
    }) as FieldDefinition;

const widgetModule: WidgetModule<CustomFieldDefinition> = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                // XNG-Changes
                // sortOrder: 10,
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                // XNG-Changes
                sortOrder: 10,
            },
            // XNG-Changes
            {
                name: fields.gapOnTablet,
                displayName: "Gap Between Items On Tablet",
                editorTemplate: "IntegerField",
                defaultValue: null,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 11,
            },
            {
                name: fields.gapOnMobile,
                displayName: "Gap Between Items On Mobile",
                editorTemplate: "IntegerField",
                defaultValue: null,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 12,
            },
            {
                name: fields.fullWidth,
                displayName: "Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            // XNG-Changes
            {
                name: fields.makeItemsTakeFullHeight,
                displayName: "Show all child Columns with the same height",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.addFooterSpace,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: advancedTab,
            },
            {
                // XNG-Changes
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // XNG-Changes: adding overlap
            {
                name: fields.allowOverlapOn,
                displayName: "Allow Overlap",
                editorTemplate: "DropDownField",
                defaultValue: "none",
                hideEmptyOption: true,
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Wide Screen Only",
                        value: "wideScreen",
                    },
                    {
                        displayName: "Transparent On Mobile",
                        value: "mobileTransparent",
                    },
                ],
                fieldType: "General",
                tab: optionsTab,
            },
            // XNG-Changes: adding overlap
            {
                name: fields.overlapPosition,
                displayName: "Overlap side (from)",
                editorTemplate: "DropDownField",
                hideEmptyOption: true,
                fieldType: "General",
                options: [
                    { displayName: "top", value: "top" },
                    { displayName: "both", value: "both" },
                    { displayName: "bottom", value: "bottom" },
                ],
                defaultValue: "both",
                isRequired: true,
                isVisible: item => item.fields.allowOverlapOn !== "none",
                tab: optionsTab,
            },
            // XNG-Changes: adding overlap
            {
                name: fields.overlapValue,
                displayName: "Overlap Value %",
                editorTemplate: "IntegerField",
                defaultValue: 70,
                max: 100,
                isRequired: true,
                fieldType: "General",
                isVisible: item => item.fields.allowOverlapOn !== "none",
                tab: optionsTab,
            },
            // XNG-Changes: adding overlap
            {
                name: fields.selectOverlapItem,
                displayName: "Select Overlap Column",
                editorTemplate: "SelectOverlapItemTemplate",
                defaultValue: "all",
                hideEmptyOption: true,
                fieldType: "General",
                isVisible: item => item.fields.allowOverlapOn !== "none",
                tab: optionsTab,
            },
            // XNG-Changes
            {
                name: fields.rowId,
                displayName: "Row ID",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 3,
                tab: advancedTab,
            },

            {
                name: fields.fullWidthContent,
                displayName: "Content Full Width",
                tooltip: "Content will expand to full screen with no side-paddings",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: ({ fields }) => fields.fullWidth,
            },
            {
                ...spaceFieldDefinition(fields.spaceTop),
            },
            {
                ...spaceFieldDefinition(fields.spaceBottom),
            },
            {
                ...spaceFieldDefinition(fields.spaceTopOnTablet),
            },
            {
                ...spaceFieldDefinition(fields.spaceBottomOnTablet),
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "white",
            },
            {
                name: fields.backgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                displayName: "Large Background Image",
            },
            {
                name: fields.mediumBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                displayName: "Medium Background Image",
            },
            {
                name: fields.smallBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                displayName: "Small Background Image",
            },
        ],
    },
};

export default widgetModule;
